import React, { useState } from 'react';
import { ChevronDown, MessageCircleQuestion, Search, HelpCircle, Phone, Mail, Sparkles } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const FAQ = () => {
  const { t, tArray, selectedCountry } = useCurrency();
  const [activeIndex, setActiveIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', label: t('faq.categories.all'), icon: Sparkles },
    { id: 'inspection', label: t('faq.categories.inspection'), icon: Search },
    { id: 'warranty', label: t('faq.categories.warranty'), icon: HelpCircle },
    { id: 'finance', label: t('faq.categories.finance'), icon: MessageCircleQuestion },
  ];

  const [activeCategory, setActiveCategory] = useState('all');

  // Get localized FAQs based on country
  const faqs = selectedCountry.code === 'MY' ? tArray('faq.questionsMY') : tArray('faq.questions');

  // Add category to each FAQ for filtering
  const faqsWithCategory = faqs.map((faq, index) => ({
    ...faq,
    category: index < 3 ? 'inspection' : index < 6 ? 'warranty' : 'finance'
  }));

  const filteredFaqs = faqsWithCategory.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.a.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFaq = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-gradient-to-b from-slate-50 to-white" id="faq">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <MessageCircleQuestion className="h-4 w-4" />
            {t('faq.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {t('faq.title')} <span className="text-blue-600">{t('faq.titleHighlight')}</span>
          </h2>
        </div>

        {/* Search Bar */}
        <div className="relative max-w-xl mx-auto mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder={t('faq.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="faq-search-input"
            className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
          />
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              data-testid={`faq-category-${cat.id}`}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${activeCategory === cat.id
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-600'
                }`}
            >
              <cat.icon className="h-4 w-4" />
              {cat.label}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="space-y-3">
          {filteredFaqs.map((faq, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${activeIndex === index
                ? 'border-blue-300 shadow-lg shadow-blue-500/10'
                : 'border-slate-200 hover:border-slate-300'
                }`}
            >
              <button
                onClick={() => toggleFaq(index)}
                data-testid={`faq-item-${index}`}
                className="w-full flex items-center justify-between p-5 text-left"
              >
                <div className="flex items-start gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${activeIndex === index ? 'bg-blue-100' : 'bg-slate-100'
                    }`}>
                    <span className={`font-bold text-sm ${activeIndex === index ? 'text-blue-600' : 'text-slate-500'
                      }`}>
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <span className={`font-semibold text-base transition-colors ${activeIndex === index ? 'text-blue-600' : 'text-slate-900'
                    }`}>
                    {faq.q}
                  </span>
                </div>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ml-4 transition-all ${activeIndex === index
                  ? 'bg-blue-600 rotate-180'
                  : 'bg-slate-100'
                  }`}>
                  <ChevronDown className={`h-4 w-4 ${activeIndex === index ? 'text-white' : 'text-slate-500'
                    }`} />
                </div>
              </button>

              {activeIndex === index && (
                <div className="px-5 pb-5">
                  <div className="ml-14 pl-4 border-l-2 border-blue-200">
                    <p className="text-slate-600 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-2">{t('faq.stillHaveQuestions')}</h3>
          <p className="text-slate-400 mb-6">{t('faq.cantFind')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${t('business.salesPhone')}`} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors">
              <Phone className="h-5 w-5" />
              {t('faq.callUs')}
            </a>
            <a href={`mailto:${t('business.salesEmail')}`} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl font-semibold transition-colors">
              <Mail className="h-5 w-5" />
              {t('faq.emailUs')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
